/**
 * CommissionerMasterGuide.js
 *
 * Commissioner-only Master Guide panel. Rendered as a tab inside CommissionerHQ.
 * Never linked from public nav. Requires password prop (X-HQ-Pass).
 *
 * Features:
 *   - Keyword search across section titles, summaries, and block bodies
 *   - Collapsible accordion grouped by nav section
 *   - Per-section commissioner notes with auto-save (1-second debounce)
 *   - Last updated (content) and last used (audit activity) timestamps
 *   - Print/PDF export via window.print()
 *   - Planned sections clearly distinguished from live ones
 */

import React, { useEffect, useState, useCallback, useRef } from 'react';

const API = process.env.REACT_APP_API_URL || '';

const HQ = {
  bg:          '#06080c',
  panel:       '#0e1218',
  panelDeep:   '#080b10',
  border:      'rgba(255, 90, 65, 0.18)',
  borderSoft:  'rgba(255,255,255,0.06)',
  text:        '#e8eaf0',
  text2:       '#8b90a0',
  text3:       '#555b6e',
  amber:       '#ff9d3a',
  red:         '#ff4757',
  green:       '#22c55e',
  blue:        '#60a5fa',
  purple:      '#a855f7',
  warning:     '#fbbf24',
};

const NAV_LABELS = {
  league:        'League',
  transactions:  'Transactions',
  intel:         'Intel',
  draft:         'Draft',
  tools:         'Tools',
  commissioner:  'Commissioner',
};

const STATUS_BADGE = {
  live:    { label: 'Live',    color: HQ.green },
  planned: { label: 'Planned', color: HQ.text3 },
  beta:    { label: 'Beta',    color: HQ.blue  },
};

function fmt(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return null;
  }
}

function fmtRelative(iso) {
  if (!iso) return null;
  try {
    const diff = Date.now() - new Date(iso).getTime();
    const days = Math.floor(diff / 86400000);
    if (days === 0) return 'today';
    if (days === 1) return 'yesterday';
    if (days < 30) return `${days}d ago`;
    const weeks = Math.floor(days / 7);
    if (weeks < 8) return `${weeks}w ago`;
    return fmt(iso);
  } catch {
    return null;
  }
}

function matches(section, q) {
  if (!q) return true;
  const needle = q.toLowerCase();
  if (section.title.toLowerCase().includes(needle)) return true;
  if (section.summary.toLowerCase().includes(needle)) return true;
  if ((section.commissioner_notes || '').toLowerCase().includes(needle)) return true;
  return (section.blocks || []).some(
    b => b.heading.toLowerCase().includes(needle) || b.body.toLowerCase().includes(needle)
  );
}

// ── Block (content row) ───────────────────────────────────────────────────────

function ContentBlock({ block }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{
        fontFamily: 'var(--font-mono)',
        fontSize: 10,
        letterSpacing: '0.25em',
        textTransform: 'uppercase',
        color: HQ.amber,
        marginBottom: 5,
      }}>
        {block.heading}
      </div>
      <div style={{
        fontSize: 13,
        color: HQ.text2,
        lineHeight: 1.7,
      }}>
        {block.body}
      </div>
    </div>
  );
}

// ── Notes editor (debounced auto-save) ───────────────────────────────────────

function NotesEditor({ sectionId, initialNotes, password }) {
  const [notes, setNotes]       = useState(initialNotes || '');
  const [status, setStatus]     = useState(null); // 'saving' | 'saved' | 'error'
  const timerRef = useRef(null);

  const save = useCallback(async (value) => {
    setStatus('saving');
    try {
      const r = await fetch(`${API}/api/hq/guide/notes/${sectionId}`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', 'X-HQ-Pass': password },
        body:    JSON.stringify({ notes: value, updated_by: 'commissioner' }),
      });
      setStatus(r.ok ? 'saved' : 'error');
    } catch {
      setStatus('error');
    }
    setTimeout(() => setStatus(null), 2000);
  }, [sectionId, password]);

  const onChange = (e) => {
    const v = e.target.value;
    setNotes(v);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => save(v), 1000);
  };

  const statusColor = status === 'saved' ? HQ.green : status === 'error' ? HQ.red : HQ.text3;
  const statusText  = status === 'saving' ? 'Saving…' : status === 'saved' ? 'Saved' : status === 'error' ? 'Error' : '';

  return (
    <div style={{ marginTop: 16, borderTop: `1px solid ${HQ.borderSoft}`, paddingTop: 14 }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6,
      }}>
        <span style={{
          fontFamily: 'var(--font-mono)',
          fontSize: 10,
          letterSpacing: '0.25em',
          textTransform: 'uppercase',
          color: HQ.purple,
        }}>
          Commissioner Notes
        </span>
        {statusText && (
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: statusColor }}>
            {statusText}
          </span>
        )}
      </div>
      <textarea
        value={notes}
        onChange={onChange}
        placeholder="Add private notes for this feature — only visible in Commissioner HQ…"
        rows={3}
        style={{
          width: '100%',
          background: HQ.panelDeep,
          border: `1px solid ${HQ.borderSoft}`,
          borderRadius: 6,
          color: HQ.text,
          fontFamily: 'var(--font-mono)',
          fontSize: 12,
          padding: '8px 10px',
          resize: 'vertical',
          outline: 'none',
          boxSizing: 'border-box',
          lineHeight: 1.6,
        }}
      />
    </div>
  );
}

// ── Section accordion card ────────────────────────────────────────────────────

function SectionCard({ section, defaultOpen, password, searchActive }) {
  const [open, setOpen] = useState(defaultOpen || false);
  const badge = STATUS_BADGE[section.status] || STATUS_BADGE.live;

  useEffect(() => {
    if (searchActive) setOpen(true);
  }, [searchActive]);

  return (
    <div style={{
      background: HQ.panel,
      border: `1px solid ${HQ.borderSoft}`,
      borderLeft: section.status === 'planned' ? `3px solid ${HQ.text3}` : `3px solid ${HQ.amber}`,
      borderRadius: 10,
      marginBottom: 8,
      overflow: 'hidden',
    }}>
      {/* Header / toggle */}
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          width: '100%',
          background: 'transparent',
          border: 'none',
          padding: '12px 16px',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          cursor: 'pointer',
          textAlign: 'left',
        }}
      >
        <span style={{ fontSize: 16, flexShrink: 0 }}>{section.icon}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontFamily: 'var(--font-display)',
            fontSize: '0.9rem',
            letterSpacing: '2px',
            color: section.status === 'planned' ? HQ.text3 : HQ.text,
            textTransform: 'uppercase',
          }}>
            {section.title}
          </div>
          <div style={{ fontSize: 12, color: HQ.text3, marginTop: 2, lineHeight: 1.4 }}>
            {section.summary}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
          <span style={{
            fontFamily: 'var(--font-mono)',
            fontSize: 9,
            letterSpacing: '0.2em',
            padding: '2px 6px',
            borderRadius: 4,
            background: `${badge.color}22`,
            color: badge.color,
            textTransform: 'uppercase',
          }}>
            {badge.label}
          </span>
          <span style={{ color: HQ.text3, fontSize: 12, fontFamily: 'var(--font-mono)' }}>
            {open ? '▲' : '▼'}
          </span>
        </div>
      </button>

      {/* Body */}
      {open && (
        <div style={{ padding: '0 16px 16px' }}>
          {/* Timestamps */}
          <div style={{
            display: 'flex', gap: 16, flexWrap: 'wrap',
            marginBottom: 16, paddingBottom: 12,
            borderBottom: `1px solid ${HQ.borderSoft}`,
          }}>
            {section.last_content_update && (
              <span style={{ fontSize: 11, color: HQ.text3, fontFamily: 'var(--font-mono)' }}>
                Updated {fmt(section.last_content_update)}
              </span>
            )}
            {section.last_used_at && (
              <span style={{ fontSize: 11, color: HQ.blue, fontFamily: 'var(--font-mono)' }}>
                Last used {fmtRelative(section.last_used_at)}
              </span>
            )}
            {section.notes_updated_at && (
              <span style={{ fontSize: 11, color: HQ.purple, fontFamily: 'var(--font-mono)' }}>
                Notes {fmtRelative(section.notes_updated_at)}
              </span>
            )}
          </div>

          {/* Content blocks */}
          {(section.blocks || []).map((b, i) => (
            <ContentBlock key={i} block={b} />
          ))}

          {/* Commissioner notes */}
          <NotesEditor
            sectionId={section.id}
            initialNotes={section.commissioner_notes}
            password={password}
          />
        </div>
      )}
    </div>
  );
}

// ── Nav group ─────────────────────────────────────────────────────────────────

function NavGroup({ navKey, sections, password, searchActive }) {
  const [collapsed, setCollapsed] = useState(false);

  if (!sections || sections.length === 0) return null;

  return (
    <div style={{ marginBottom: 24 }}>
      <button
        onClick={() => setCollapsed(c => !c)}
        style={{
          background: 'transparent',
          border: 'none',
          width: '100%',
          textAlign: 'left',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '6px 0',
          cursor: 'pointer',
          marginBottom: 10,
          borderBottom: `1px solid ${HQ.border}`,
          paddingBottom: 8,
        }}
      >
        <span style={{
          fontFamily: 'var(--font-display)',
          fontSize: '0.8rem',
          letterSpacing: '4px',
          color: HQ.amber,
          textTransform: 'uppercase',
          flex: 1,
        }}>
          {NAV_LABELS[navKey] || navKey}
        </span>
        <span style={{
          fontFamily: 'var(--font-mono)',
          fontSize: 10,
          color: HQ.text3,
          letterSpacing: '0.1em',
        }}>
          {sections.length} section{sections.length !== 1 ? 's' : ''} {collapsed ? '▼' : '▲'}
        </span>
      </button>

      {!collapsed && sections.map(s => (
        <SectionCard
          key={s.id}
          section={s}
          password={password}
          searchActive={searchActive}
        />
      ))}
    </div>
  );
}

// ── Main panel ────────────────────────────────────────────────────────────────

export default function CommissionerMasterGuide({ password }) {
  const [guide, setGuide]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState(null);
  const [query, setQuery]   = useState('');

  useEffect(() => {
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const r = await fetch(`${API}/api/hq/guide`, {
          headers: { 'X-HQ-Pass': password },
        });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        setGuide(await r.json());
      } catch (e) {
        setError(e.message);
      }
      setLoading(false);
    })();
  }, [password]);

  const handlePrint = () => window.print();

  if (loading) {
    return (
      <div style={{ color: HQ.text3, fontFamily: 'var(--font-mono)', fontSize: 13, padding: 20 }}>
        Loading Master Guide…
      </div>
    );
  }
  if (error) {
    return (
      <div style={{ color: HQ.red, fontFamily: 'var(--font-mono)', fontSize: 13, padding: 20 }}>
        Failed to load Master Guide: {error}
      </div>
    );
  }
  if (!guide) return null;

  const { sections, nav_order } = guide;
  const q         = query.trim().toLowerCase();
  const filtered  = q ? sections.filter(s => matches(s, q)) : sections;
  const searching = !!q;

  // Group filtered sections by nav
  const byNav = {};
  (nav_order || []).forEach(nav => { byNav[nav] = []; });
  filtered.forEach(s => {
    const nav = s.nav_section;
    if (!byNav[nav]) byNav[nav] = [];
    byNav[nav].push(s);
  });

  const liveCount    = sections.filter(s => s.status === 'live').length;
  const plannedCount = sections.filter(s => s.status === 'planned').length;

  return (
    <div>
      {/* Header bar */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 12,
        marginBottom: 20,
      }}>
        <div>
          <div style={{
            fontFamily: 'var(--font-mono)',
            fontSize: 10,
            letterSpacing: '0.3em',
            color: HQ.amber,
            textTransform: 'uppercase',
            marginBottom: 4,
          }}>
            Commissioner Only
          </div>
          <h2 style={{
            fontFamily: 'var(--font-display)',
            fontSize: '1.1rem',
            letterSpacing: '4px',
            color: HQ.text,
            textTransform: 'uppercase',
            margin: 0,
          }}>
            Master Guide
          </h2>
          <div style={{ fontSize: 11, color: HQ.text3, marginTop: 4 }}>
            {liveCount} live feature{liveCount !== 1 ? 's' : ''} · {plannedCount} planned
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Search */}
          <input
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search features…"
            style={{
              background: HQ.panelDeep,
              border: `1px solid ${query ? HQ.amber + '88' : HQ.borderSoft}`,
              borderRadius: 6,
              color: HQ.text,
              fontFamily: 'var(--font-mono)',
              fontSize: 12,
              padding: '6px 12px',
              outline: 'none',
              width: 200,
            }}
          />
          {query && (
            <button
              onClick={() => setQuery('')}
              style={{
                background: 'transparent',
                border: 'none',
                color: HQ.text3,
                cursor: 'pointer',
                fontSize: 16,
                padding: '0 4px',
              }}
            >
              ×
            </button>
          )}
          {/* Print */}
          <button
            onClick={handlePrint}
            style={{
              background: 'transparent',
              border: `1px solid ${HQ.borderSoft}`,
              color: HQ.text2,
              padding: '6px 14px',
              borderRadius: 6,
              fontFamily: 'var(--font-mono)',
              fontSize: 11,
              letterSpacing: '0.2em',
              textTransform: 'uppercase',
              cursor: 'pointer',
            }}
          >
            Print / PDF
          </button>
        </div>
      </div>

      {/* Search result count */}
      {searching && (
        <div style={{
          fontFamily: 'var(--font-mono)',
          fontSize: 11,
          color: filtered.length > 0 ? HQ.blue : HQ.red,
          marginBottom: 16,
          letterSpacing: '0.1em',
        }}>
          {filtered.length > 0
            ? `${filtered.length} section${filtered.length !== 1 ? 's' : ''} match "${q}"`
            : `No sections match "${q}"`}
        </div>
      )}

      {/* Section groups */}
      {(nav_order || []).map(nav => (
        <NavGroup
          key={nav}
          navKey={nav}
          sections={byNav[nav] || []}
          password={password}
          searchActive={searching}
        />
      ))}
    </div>
  );
}
